import { Box, Column, SimpleLabel, Text } from '@hopskipdrive/react-components';
import { isSameDay } from 'date-fns';
import { includes } from 'lodash';

import { RIDE_STATES } from '../../../constants';
import { useTimezone } from '../../../hooks/useTimezone';
import { RiderServices, SERVICE_ICON_CODES } from '../../../riders/features/RiderServices';
import { getFirstNameLastInitial } from '../../../utils/helpers';

import { RemoveRidersModal } from './RemoveRidersModal';

export const useRemovedRidersTableColumn = ({ reset }: { reset: () => void }) => {
  const { formatInTimezone, getDateInTimezone } = useTimezone();

  return [
    statusColumn,
    passengersColumn,
    {
      key: 'startsAt',
      label: 'Start Date / Time',
      align: 'left',
      valueSetter: (cellValue) => cellValue && formatInTimezone(cellValue, 'EEE, MM/dd, hh:mm aa'),
      minWidth: '167px',
    },
    {
      key: 'removedBy',
      label: 'Removed by',
      align: 'left',
    },
    {
      key: 'reason',
      label: 'Reason',
      align: 'left',
      valueGetter: (rowData) => (
        <span aria-label={rowData.removalReason ? undefined : 'Reason Empty'}>{rowData.removalReason}</span>
      ),
    },
    {
      key: 'removedAt',
      label: 'Removal Date',
      align: 'left',
      valueSetter: (cellValue) => (
        <span aria-label={cellValue ? undefined : 'No Removal Date'}>
          {cellValue && formatInTimezone(cellValue, 'EEE, MM/dd, hh:mm aa')}
        </span>
      ),
      minWidth: '167px',
    },
    {
      key: 'fare',
      label: 'Fare',
      align: 'left',
    },
    {
      key: 'services',
      label: 'Services',
      align: 'left',
      valueGetter: (rowData) => (
        <span aria-label={getRiderServices(rowData) ? undefined : 'Services Empty'}>{getRiderServices(rowData)}</span>
      ),
      minWidth: '167px',
    },
    {
      label: 'Actions',
      align: 'left',
      disableCellClick: true,
      valueGetter: (rowData) =>
        isSameDay(getDateInTimezone(rowData.startsAt), getDateInTimezone()) &&
        rowData.hasSameDayRides && (
          <RemoveRidersModal slugs={[rowData.passengers[0].slug]} afterRemoveRiderFromRides={reset} />
        ),
    },
  ] as Column[];
};

const statusColumn = {
  label: 'Status',
  align: 'left',
  valueGetter: (rowData) => getStatusLabel(rowData),
};

const passengersColumn = {
  key: 'passengers',
  label: 'Removed Rider',
  align: 'left',
  maxWidth: '200px',
  valueSetter: (cellValue) =>
    cellValue && (
      <Text noWrap>
        {`${cellValue.length}\xa0\xa0\xa0${cellValue
          .map((rider) => getFirstNameLastInitial(rider.fullName))
          .join(', ')}`}
      </Text>
    ),
};

const getStatusLabel: React.FC<{ status: string }> = ({ status }) => {
  switch (true) {
    case includes(RIDE_STATES.inProgress, status):
      return <SimpleLabel label="In Progress" textColor="text" bgColor="light-green" />;
    case includes(RIDE_STATES.completed, status):
      return <SimpleLabel ml={2} label="Completed" textColor="text" bgColor="light-purple" />;
    default:
      null;
  }

  return null;
};

const getRiderServices = (rowData) => {
  if (!rowData?.services || !Array.isArray(rowData.services)) {
    return null;
  }

  const validCodes = SERVICE_ICON_CODES;
  const services = rowData.services.filter((service) => validCodes.includes(service.code));

  if (services.length === 0) {
    return null;
  }

  return (
    <Box display="flex" gap="4px">
      <RiderServices services={services} />
    </Box>
  );
};
